import { type HcpDocumentStatusValue } from "@clipboard-health/contract-documents-service";
import { keyBy } from "lodash";
import { useEffect, useState } from "react";
import { type Control, type FieldErrors, type FieldValues, useForm } from "react-hook-form";

import {
  type EvidenceData,
  type EvidenceDefinition,
  type JsonSchema,
  toJsonSchema,
} from "../../types";
import { cleanProperties, isUnknownEvidenceDefinition } from "../../utils";

const APPROVED_STATUS: HcpDocumentStatusValue = "APPROVED";

export interface UseEvidenceFormArguments {
  evidence: EvidenceData | undefined;
  definitions: EvidenceDefinition[];
  documentStatus: HcpDocumentStatusValue | undefined;
}

export interface EvidenceFormSnapshot {
  evidenceId: string | undefined;
  evidenceDefinitionId: string;
  isUnknownDefinitionSelected: boolean;
  hasLinkedEvidence: boolean;
  isEvidenceApproved: boolean;
  isApprovalBlocked: boolean;
}

export interface EvidenceFormFieldProps {
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
  definitions: EvidenceDefinition[];
  selectedDefinitionId: string;
  selectedDefinition: EvidenceDefinition | undefined;
  propertySchema: JsonSchema | undefined;
  hasProperties: boolean;
  onDefinitionChange: (definitionId: string) => void;
  isDefinitionMissing: boolean;
}

export interface UseEvidenceFormReturn {
  viewProps: EvidenceFormFieldProps;
  snapshot: EvidenceFormSnapshot;
  validate: () => Promise<boolean>;
  getCleanedProperties: () => Record<string, unknown>;
}

export function useEvidenceForm(input: UseEvidenceFormArguments): UseEvidenceFormReturn {
  const { evidence, definitions, documentStatus } = input;

  // Two state primitives, each matching the nature of its data:
  //   - `evidenceDefinitionId`: a single typed string. `useState` keeps it
  //     fully typed end-to-end; no RHF `Control<T>` invariance to fight.
  //   - `properties`: dynamic JSON-schema-driven payload. RHF<FieldValues>
  //     handles arbitrary field paths registered at runtime by DynamicSchemaForm.
  // The only remaining cast is on `getValues("properties")`, which is inherent
  // to the dynamic shape — TS can't statically know what the schema dictates.
  const [evidenceDefinitionId, setEvidenceDefinitionId] = useState("");
  const propertiesForm = useForm<FieldValues>({
    mode: "onSubmit",
    defaultValues: { properties: {} },
  });

  const evidenceId = evidence?.id;
  const evidenceLinkedDefinitionId = evidence?.relationships.evidenceDefinition.data.id;

  useEffect(() => {
    if (!evidence) {
      return;
    }
    setEvidenceDefinitionId(evidence.relationships.evidenceDefinition.data.id);
    propertiesForm.reset({ properties: evidence.attributes.properties });
    // propertiesForm.reset is stable across renders; re-run only when the
    // evidence identity (or its linked definition) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceId, evidenceLinkedDefinitionId]);

  const definitionsById = keyBy(definitions, "id");
  const selectedDefinition: EvidenceDefinition | undefined = definitionsById[evidenceDefinitionId];
  const propertySchema = selectedDefinition
    ? toJsonSchema(selectedDefinition.attributes.propertySchema)
    : undefined;
  const hasProperties = propertySchema
    ? Object.keys(propertySchema.properties ?? {}).length > 0
    : false;

  const isUnknownDefinitionSelected = isUnknownEvidenceDefinition(selectedDefinition);
  const hasLinkedEvidence = evidence !== undefined;
  const isDefinitionMissing =
    hasLinkedEvidence &&
    evidenceLinkedDefinitionId !== undefined &&
    definitions.length > 0 &&
    !(evidenceLinkedDefinitionId in definitionsById);
  const isEvidenceApproved =
    hasLinkedEvidence && evidenceDefinitionId !== "" && documentStatus === APPROVED_STATUS;
  const isApprovalBlocked =
    hasLinkedEvidence &&
    documentStatus === APPROVED_STATUS &&
    (evidenceDefinitionId === "" || isUnknownDefinitionSelected || isDefinitionMissing);

  function onDefinitionChange(definitionId: string) {
    setEvidenceDefinitionId(definitionId);
    propertiesForm.reset({ properties: {} });
  }

  async function validate(): Promise<boolean> {
    if (!isEvidenceApproved) {
      return true;
    }
    return propertiesForm.trigger();
  }

  function getCleanedProperties(): Record<string, unknown> {
    const properties = propertiesForm.getValues("properties") as Record<string, unknown>;
    if (!propertySchema) {
      return properties;
    }
    return cleanProperties(properties, propertySchema);
  }

  return {
    viewProps: {
      control: propertiesForm.control,
      errors: propertiesForm.formState.errors,
      definitions,
      selectedDefinitionId: evidenceDefinitionId,
      selectedDefinition,
      propertySchema,
      hasProperties,
      onDefinitionChange,
      isDefinitionMissing,
    },
    snapshot: {
      evidenceId,
      evidenceDefinitionId,
      isUnknownDefinitionSelected,
      hasLinkedEvidence,
      isEvidenceApproved,
      isApprovalBlocked,
    },
    validate,
    getCleanedProperties,
  };
}
